import React, { useEffect } from 'react';
import {
  $setLevelConfigBody,
  SetLevelConfigBody,
} from '@loyalty/shared-common/gamification-api-client';
import { Box, Button, Dialog, DialogContent, IconButton, Tooltip } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfFormRow } from '@qualifio/ui-commons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { useTranslation } from 'react-i18next';
import styles from './LevelsConfigDialog.module.scss';
import { CurrencyDropdown } from '../../../currency/components/currency-dropdown';
import { ExpirationForm } from '../../../expiration/expiration-form';
import { Loader } from '../../../shared/layout/Loader';
import { Title } from '../../../shared/layout/Title';
import { useCurrencies } from '../../../currency/CurrenciesContext';
import { useFeatures } from '../../../features/FeaturesContext';
import { useJoiResolver } from '../../../shared/forms/use-joi-resolver';
import { useLevelsConfig } from '../../LevelsConfigContext';

export function LevelsConfigDialog({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
}) {
  const { levelsConfig, setLevelConfig, isLoading } = useLevelsConfig();
  const { currencies } = useCurrencies();
  const { features } = useFeatures();
  const { write } = features[Feature.LEVEL_CONFIG];
  const { t } = useTranslation();
  const resolver = useJoiResolver($setLevelConfigBody);
  const form = useForm<SetLevelConfigBody>({
    mode: 'onChange',
    resolver,
  });
  const {
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = form;

  const loading = isLoading('setLevelConfig');

  const onSubmit = async (newLevelConfig: SetLevelConfigBody) => {
    const success = await setLevelConfig(newLevelConfig);
    success && close();
  };

  function close() {
    form.reset(levelsConfig);
    setOpen(false);
  }

  useEffect(() => {
    reset({
      expiration: levelsConfig?.expiration,
      currencyId: levelsConfig?.currencyId,
    });
  }, [levelsConfig]);

  const isEdit = !!levelsConfig;
  const selectedCurrencyId = watch('currencyId');
  const isCurrencyArchived = currencies.some(
    currency => currency.id === selectedCurrencyId && currency.deletedAt,
  );

  return (
    <Dialog onClose={close} open={open} fullWidth={true} maxWidth="sm">
      <IconButton
        onClick={close}
        sx={{
          position: 'absolute',
          right: 0,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogContent className={styles.content}>
        <Box textAlign="center">
          <Title>
            {isEdit ? t('levels.configDialog.edit.title') : t('levels.configDialog.create.title')}
          </Title>
          <Box mb={1}>
            {isEdit
              ? t('levels.configDialog.edit.description')
              : t('levels.configDialog.create.description')}
          </Box>
        </Box>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)} id="level-config-id">
            <Controller
              control={form.control}
              name="currencyId"
              defaultValue=""
              render={({ field: { ref, ...field } }) => (
                <QlfFormRow label={t('levels.inputs.currency.label')}>
                  <CurrencyDropdown fullWidth {...field} />
                </QlfFormRow>
              )}
            />

            <ExpirationForm kind={'level'} />

            {!isEdit && (
              <Box textAlign="center">
                <p>{t('levels.configDialog.create.info')}</p>
              </Box>
            )}
            <Box display="flex" justifyContent="center" mt={1}>
              <Button type="button" color="grey" onClick={close}>
                {isEdit
                  ? t('levels.configDialog.edit.buttons.cancel')
                  : t('levels.configDialog.create.buttons.cancel')}
              </Button>
              <Tooltip title={write ? '' : t('shared.action.permission.required')}>
                <div>
                  <Button
                    type="submit"
                    color="success"
                    disabled={loading || !isValid || isCurrencyArchived || !write}
                    className={styles.submit}
                  >
                    {isEdit
                      ? t('levels.configDialog.edit.buttons.submit')
                      : t('levels.configDialog.create.buttons.submit')}
                    {loading && <Loader />}
                  </Button>
                </div>
              </Tooltip>
            </Box>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
}
